// ./src/hooks/useToast.ts
import { useCallback, useMemo } from "react";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
} from "@tabler/icons-react";
import React from "react";
import { useTranslation } from "react-i18next";

export interface ToastOptions {
  /** Toast title */
  title?: string;
  /** Auto-close delay in ms (default: 5000, set to false to disable) */
  autoClose?: number | false;
  /** Whether the toast can be closed by clicking */
  withCloseButton?: boolean;
}

export interface UseToastReturn {
  /** Show a success toast */
  success: (message: string, options?: ToastOptions) => void;
  /** Show an error toast */
  error: (message: string, options?: ToastOptions) => void;
  /** Show a warning toast */
  warning: (message: string, options?: ToastOptions) => void;
  /** Show an info toast */
  info: (message: string, options?: ToastOptions) => void;
  /** Hide all toasts */
  hideAll: () => void;
}

const DEFAULT_AUTO_CLOSE = 3000;
const ERROR_AUTO_CLOSE = 8000;

/** What an error toast says when the caller had nothing to say — never a red box with an empty body (#146). */
export const TOAST_ERROR_FALLBACK = "Something went wrong. Please try again.";

/** The body of an error toast: the caller's message, or the fallback when it is empty. Pure — `check-error-readers` runs it. */
export const toastErrorMessage = (message: unknown, fallback: string = TOAST_ERROR_FALLBACK): string =>
  (typeof message === "string" ? message.trim() : "") || fallback.trim() || TOAST_ERROR_FALLBACK;

export const useToast = (): UseToastReturn => {
  const { t } = useTranslation(["common"]);

  const success = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Success",
      message,
      color: "green",
      icon: React.createElement(IconCheck, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const error = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Error",
      message: toastErrorMessage(message, t("common:errors.tryAgain", { defaultValue: TOAST_ERROR_FALLBACK })),
      color: "red",
      icon: React.createElement(IconX, { size: 18 }),
      autoClose: options?.autoClose ?? ERROR_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, [t]);

  const warning = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Warning",
      message,
      color: "yellow",
      icon: React.createElement(IconAlertTriangle, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const info = useCallback((message: string, options?: ToastOptions) => {
    notifications.show({
      title: options?.title ?? "Info",
      message,
      color: "blue",
      icon: React.createElement(IconInfoCircle, { size: 18 }),
      autoClose: options?.autoClose ?? DEFAULT_AUTO_CLOSE,
      withCloseButton: options?.withCloseButton ?? true,
    });
  }, []);

  const hideAll = useCallback(() => {
    notifications.cleanQueue();
    notifications.clean();
  }, []);

  // Memoize the returned object so its identity is STABLE across renders. The
  // methods are already useCallback-stable; without this useMemo the wrapping
  // object was a new reference every render, so any consumer that lists `toast`
  // in a useMemo/useCallback/useEffect dependency array recreated that value every
  // render — causing effect-fires-every-render infinite loops ("Maximum update
  // depth exceeded"). Keep it memoized.
  return useMemo(
    () => ({ success, error, warning, info, hideAll }),
    [success, error, warning, info, hideAll],
  );
};
